import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { api, type CardValues } from "../lib/api";

interface CardValuesOverlayProps {
  open: boolean;
  onClose: () => void;
}

interface CardValueTableProps {
  title: string;
  ranks: string[];
  values: Record<string, number>;
}

const RANK_NAMES: Record<string, string> = {
  A: "As",
  K: "Roi",
  Q: "Dame",
  J: "Valet",
};

function CardValueTable({ title, ranks, values }: CardValueTableProps) {
  const cards = [...ranks].sort(
    (a, b) => values[b] - values[a] || ranks.indexOf(b) - ranks.indexOf(a),
  );
  return (
    <section className="card space-y-3">
      <h2 className="text-lg font-bold text-ink">{title}</h2>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] border-collapse text-center text-sm">
          <tbody>
            <tr className="border-b border-gray-200">
              <th className="px-2 py-2 text-left font-semibold text-smoke">
                Carte
              </th>
              {cards.map((card) => (
                <th key={card} className="px-2 py-2 font-semibold text-ink">
                  {RANK_NAMES[card] ?? card}
                </th>
              ))}
            </tr>
            <tr>
              <th className="px-2 py-2 text-left font-semibold text-smoke">
                Points
              </th>
              {cards.map((card) => (
                <td key={card} className="px-2 py-2 tabular-nums">
                  {values[card]}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function CardValuesOverlay({ open, onClose }: CardValuesOverlayProps) {
  const titleId = useId();
  const [cardValues, setCardValues] = useState<CardValues | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCardValues(null);
    setFailed(false);
    api.getCardValues().then(
      (values) => {
        if (!cancelled) setCardValues(values);
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, attempt]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-y-auto bg-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
          <h1 id={titleId} className="text-xl font-bold text-ink">
            Valeur des cartes
          </h1>
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember"
            aria-label="Fermer"
            onClick={onClose}
            autoFocus
          >
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6 sm:px-6">
        <p className="text-sm text-smoke">
          Le dernier pli ajoute 10 points (« dix de der »). Le total de la donne
          est toujours de 162 points.
        </p>

        {failed ? (
          <div role="alert" className="space-y-3">
            <p>Impossible de charger la valeur des cartes.</p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setAttempt((value) => value + 1)}
            >
              Réessayer
            </button>
          </div>
        ) : cardValues ? (
          <>
            <CardValueTable
              title="Couleur — à l’atout"
              ranks={cardValues.ranks}
              values={cardValues.trump}
            />
            <CardValueTable
              title="Couleur — hors atout"
              ranks={cardValues.ranks}
              values={cardValues.plain}
            />
            <CardValueTable
              title="Sans Atout"
              ranks={cardValues.ranks}
              values={cardValues.noTrump}
            />
            <CardValueTable
              title="Tout Atout"
              ranks={cardValues.ranks}
              values={cardValues.allTrump}
            />
          </>
        ) : (
          <p role="status">Chargement des valeurs…</p>
        )}
      </main>
    </div>,
    document.body,
  );
}
